import { useState, useRef, useEffect } from "react";
import { type ChatMessage, type AgentPhase } from "../hooks/useSpaceAgent";
import { parseMessageSegments } from "../spacesLatexRenderUtils";

type Props = {
  messages: ChatMessage[];
  agentPhase: AgentPhase;
  isRecording: boolean;
  sendMessage: (text: string) => void;
};

function preprocessAgentText(text: string): string {
  return text.replace(/--/g, "…");
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words font-mulish ${
          isUser
            ? "font-semibold bg-lightgreen text-white rounded-br-sm"
            : "font-medium bg-[#F7F6FF] text-gray-800 rounded-bl-sm"
        }`}
      >
        {isUser
          ? msg.text || <span className="opacity-40 italic">…</span>
          : (() => {
              const segments = parseMessageSegments(
                preprocessAgentText(msg.text),
              );
              return segments.length === 0 ? (
                <span className="opacity-40 italic">…</span>
              ) : (
                segments.map((seg, index) =>
                  seg.type === "latex" ? (
                    <span
                      key={index}
                      dangerouslySetInnerHTML={{ __html: seg.renderedHtml }}
                    />
                  ) : (
                    <span key={index}>{seg.content}</span>
                  ),
                )
              );
            })()}
      </div>
    </div>
  );
}

export default function SpaceAgentChat({
  messages,
  agentPhase,
  isRecording,
  sendMessage,
}: Props) {
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const isBusy = agentPhase !== "idle";

  const isLoading = agentPhase === "loading";

  // Hide empty assistant bubbles while streaming — CANVAS_START creates one before
  // any text arrives, which would flash an empty placeholder. Show dots instead.
  const visibleMessages = messages.filter(
    (msg) => !(isLoading && msg.role === "assistant" && !msg.text.trim()),
  );
  const lastVisibleMessage = visibleMessages[visibleMessages.length - 1];
  const isShowingDots = isLoading && lastVisibleMessage?.role !== "assistant";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, agentPhase]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || isBusy) return;
    sendMessage(text);
    setInput("");
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2 min-h-0">
        {visibleMessages.length === 0 && (
          <p className="text-xs text-gray-400 text-center mt-4">
            Ask the AI tutor anything about the lesson.
          </p>
        )}
        {visibleMessages.map((msg) => (
          <MessageBubble key={msg.id} msg={msg} />
        ))}
        {isShowingDots && (
          <div className="flex justify-start">
            <div className="bg-[#F7F6FF] rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5 items-center">
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="w-1 h-1 rounded-full bg-gray-400 animate-bounce"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="px-3 py-2 border-t shrink-0 flex gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isBusy || isRecording}
          placeholder={isRecording ? "Listening…" : "Ask something…"}
          className="flex-1 bg-gray-50 text-gray-900 text-sm rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-lightgreen selection:bg-lightgreen/30 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isBusy || isRecording || !input.trim()}
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-lightgreen transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="white"
            className="w-4 h-4"
          >
            <path d="M3.478 2.405a.75.75 0 0 0-.926.94l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.405Z" />
          </svg>
        </button>
      </form>
    </>
  );
}
